import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, effect, inject } from '@angular/core';
import { Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { FromDictionaryPipe, LanguageService } from '@teamfoster/sdk/dictionary-ngrx';
import { DynamicFormField } from '@teamfoster/dynamic-forms';
import { Address } from 'cluster';
import { Subject } from 'rxjs';
import { DialogConfirmComponent } from 'src/app/core/components/dialog-confirm/dialog-confirm.component';
import { Breadcrumb } from 'src/app/core/models/breadcrumb.model';
import {
  AddAddress,
  DeleteAddress,
  getAddressesErrors,
  getAddressesLoading,
  getAddressesUpdatet,
  getCustomerDefaultAddress,
  getCustomerLoading,
  getSelectedAddress,
  getShipsToCountries,
  ResetAddressLoadingStates,
  UpdateAddress,
} from 'src/app/shop';

import * as countries from 'i18n-iso-countries';
import nl from 'i18n-iso-countries/langs/nl.json';
import { cleanAddressId } from 'src/app/shop/services';

@Component({
  selector: 'app-account-edit-addresses',
  templateUrl: './account-edit-addresses.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AccountEditAddressesComponent implements OnInit, OnDestroy {
  private lang = inject(LanguageService);
  private dict = new FromDictionaryPipe(this.lang);
  private store = inject(Store);
  private dialog = inject(MatDialog);
  private _unsubscribe = new Subject<void>();
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  // private localeCountries = countries.registerLocale(require('i18n-iso-countries/langs/nl.json'));
  private localeCountries = countries.registerLocale(nl);

  prefix: string;
  id: string;
  idV2: string | undefined;
  type: 'bewerken' | 'toevoegen';
  formFields: DynamicFormField[] = [];

  address$ = this.store.selectSignal(getSelectedAddress);
  addressUpdating$ = this.store.selectSignal(getAddressesLoading);
  addressUpdateSuccess$ = this.store.selectSignal(getAddressesUpdatet);
  addressUpdateErrors$ = this.store.selectSignal(getAddressesErrors);
  shipsToCountries$ = this.store.selectSignal(getShipsToCountries);
  customerDefaultAddress$ = this.store.selectSignal(getCustomerDefaultAddress);
  customerLoading$ = this.store.selectSignal(getCustomerLoading);

  constructor() {
    this.prefix = `adres-${this.route.snapshot.data['type']}`;
    this.id = this.route.snapshot.params['id'];
    this.type = this.route.snapshot.data['type'];
  }

  isDefaultAddress$ = computed(() => {
    const customer = this.customerDefaultAddress$();
    const address = this.address$();
    const loading = this.customerLoading$();

    if (loading) return false;

    if (address && customer) {
      return cleanAddressId(customer.id) === cleanAddressId(address.id);
    } else {
      return false;
    }
  });

  addressSetForm$ = effect(() => {
    const address = this.address$();
    // const shipToCountries = this.shipsToCountries$();
    const countries = this.shipsToCountries$();

    if (!countries) return;

    // console.log(countries, this.localeCountries);

    this.idV2 = address?.id;

    this.formFields = [
      {
        inputType: 'text',
        label: this.dict.transform(`formulier-veld-label-voornaam`),
        name: 'firstName',
        validators: [Validators.required],
        autocomplete: 'given-name',
        placeholder: this.dict.transform(`formulier-veld-placeholder-voornaam`, false),
        fieldType: 'input',
        order: 1,
        value: address?.firstName,
        cssClass: 'col-12 col-md-6',
      },
      {
        inputType: 'text',
        label: this.dict.transform(`formulier-veld-label-achternaam`),
        name: 'lastName',
        validators: [Validators.required],
        autocomplete: 'family-name',
        placeholder: this.dict.transform(`formulier-veld-placeholder-achternaam`, false),
        fieldType: 'input',
        order: 1,
        value: address?.lastName,
        cssClass: 'col-12 col-md-6',
      },
      {
        inputType: 'text',
        label: this.dict.transform(`formulier-veld-label-bedrijf`),
        name: 'company',
        validators: [],
        fieldType: 'input',
        placeholder: this.dict.transform(`formulier-veld-placeholder-bedrijf`, false),
        hint: this.dict.transform(`formulier-veld-hint-bedrijf`, false),
        order: 1,
        value: address?.company,
      },
      {
        inputType: 'text',
        label: this.dict.transform(`formulier-veld-label-adres1`),
        name: 'address1',
        validators: [Validators.required],
        fieldType: 'input',
        placeholder: this.dict.transform(`formulier-veld-placeholder-adres1`, false),
        hint: this.dict.transform(`formulier-veld-hint-adres1`, false),
        order: 1,
        value: address?.address1,
      },
      {
        inputType: 'text',
        label: this.dict.transform(`formulier-veld-label-adres2`),
        name: 'address2',
        validators: [Validators.required],
        fieldType: 'input',
        placeholder: this.dict.transform(`formulier-veld-placeholder-adres2`, false),
        hint: this.dict.transform(`formulier-veld-hint-adres2`, false),
        order: 1,
        value: address?.address2,
      },
      {
        inputType: 'text',
        label: this.dict.transform(`formulier-veld-label-stad`),
        name: 'city',
        validators: [Validators.required],
        fieldType: 'input',
        placeholder: this.dict.transform(`formulier-veld-placeholder-stad`, false),
        hint: this.dict.transform(`formulier-veld-hint-stad`, false),
        order: 1,
        value: address?.city,
      },
      {
        label: this.dict.transform(`formulier-veld-label-land`),
        name: 'country',
        fieldType: 'select',
        validators: [Validators.required],
        placeholder: this.dict.transform(`formulier-veld-placeholder-land`, false),
        hint: this.dict.transform(`formulier-veld-hint-land`, false),
        order: 1,
        value: address?.countryCodeV2,
        selectOptions: (countries || []).map(code => ({
          title: this.getCountryName(code),
          id: code,
        })),
      },
      {
        inputType: 'text',
        label: this.dict.transform(`formulier-veld-label-postcode`),
        name: 'zip',
        validators: [Validators.required],
        fieldType: 'input',
        placeholder: this.dict.transform(`formulier-veld-placeholder-postcode`, false),
        hint: this.dict.transform(`formulier-veld-hint-postcode`, false),
        order: 1,
        value: address?.zip,
      },
      {
        inputType: 'text',
        label: this.dict.transform(`formulier-veld-label-telefoon`),
        name: 'phone',
        validators: [],
        fieldType: 'input',
        placeholder: this.dict.transform(`formulier-veld-placeholder-telefoon`, false),
        hint: this.dict.transform(`formulier-veld-hint-telefoon`, false),
        order: 1,
        value: address?.phone,
      },
    ];
  });

  ngOnInit(): void {
    this.store.dispatch(ResetAddressLoadingStates());
  }

  getCountryName(code: string) {
    // console.log(code, countries.getName(code, 'nl'));
    return countries.getName(code, 'nl', { select: 'alias' }) || code;
  }

  get breadcrumbs(): Breadcrumb[] {
    return [
      {
        label: this.dict.transform(`account-menu-titel`),
        routerLink: ['/account'],
      },
      {
        label: this.dict.transform(`account-adres-${this.type}`),
        routerLink: [],
      },
    ];
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  handleSubmit(formValue: Address) {
    if (this.type === 'toevoegen') {
      this.store.dispatch(AddAddress({ address: formValue, redirect: ['/', 'account'] }));
    } else if (this.idV2) {
      this.store.dispatch(UpdateAddress({ address: formValue, id: this.idV2 }));
    }
  }

  deleteAddress() {
    const dialogRef = this.dialog.open(DialogConfirmComponent, {
      data: {
        title: this.dict.transform('adres-verwijder-dialoog-titel'),
        text: this.dict.transform('adres-verwijder-dialoog-tekst'),
        actionLabels: {
          proceed: this.dict.transform('adres-verwijder-dialoog-verwijderen-bevestigen'),
        },
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.store.dispatch(DeleteAddress({ id: this.idV2 || '', redirect: ['/', 'account', 'profiel'] }));
      }
    });
  }
}
